import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CuentaSelectSearch } from "../../features/finance/catalogo/components/CuentaSelect";
import { overlayClose } from "../utils/overlayClose";

const LABEL_STYLE = {
  display: "block", fontSize: "0.65rem", fontWeight: 700,
  color: "var(--text-muted)", textTransform: "uppercase",
  letterSpacing: "0.4px", marginBottom: 4,
};

/**
 * Modal para desglosar el monto de una factura en varios rubros (cada uno
 * con su propia cuenta contable). Al confirmar, el total se calcula sumando
 * los rubros — el usuario NUNCA digita el monto total directamente.
 *
 * tipo: "INGRESO" | "GASTO" — filtra las cuentas disponibles en cada fila.
 * initialRubros: [{ cuenta_ingreso|cuenta_gasto, concepto, monto }] para editar.
 * onConfirm(rubros, total) — rubros con forma { cuenta, cuentaObj, concepto, monto }.
 */
export default function RubrosModal({ open, onClose, tipo, initialRubros, onConfirm }) {
  const { t } = useTranslation();
  const campoCuenta = tipo === "GASTO" ? "cuenta_gasto" : "cuenta_ingreso";

  const [filas, setFilas] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initialRubros && initialRubros.length > 0) {
      setFilas(initialRubros.map((r, i) => ({
        key: `r-${i}-${Date.now()}`,
        cuenta: r[campoCuenta]?.id ?? r[campoCuenta] ?? null,
        cuentaObj: r[campoCuenta] && typeof r[campoCuenta] === "object" ? r[campoCuenta] : null,
        concepto: r.concepto || "",
        monto: r.monto != null ? String(r.monto) : "",
      })));
    } else {
      setFilas([
        { key: `r-0-${Date.now()}`, cuenta: null, cuentaObj: null, concepto: "", monto: "" },
      ]);
    }
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  function agregarFila() {
    setFilas((p) => [...p, { key: `r-${p.length}-${Date.now()}`, cuenta: null, cuentaObj: null, concepto: "", monto: "" }]);
  }

  function quitarFila(key) {
    setFilas((p) => (p.length > 1 ? p.filter((f) => f.key !== key) : p));
  }

  function actualizarFila(key, cambios) {
    setFilas((p) => p.map((f) => (f.key === key ? { ...f, ...cambios } : f)));
  }

  const total = filas.reduce((acc, f) => acc + (Number(f.monto) || 0), 0);

  function confirmar() {
    const incompleta = filas.find((f) => !f.cuenta || !(Number(f.monto) > 0));
    if (incompleta) {
      setError(t("rubros.faltanDatos"));
      return;
    }
    if (total <= 0) {
      setError(t("rubros.totalInvalido"));
      return;
    }
    const rubrosOut = filas.map((f) => ({
      [campoCuenta]: f.cuenta,
      cuentaObj: f.cuentaObj,
      concepto: f.concepto,
      monto: Number(f.monto),
    }));
    onConfirm(rubrosOut, total);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000,
      padding: 16,
    }} {...overlayClose(onClose)}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "88vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h3 style={{ margin: 0, fontSize: "1.05rem", color: "var(--text-primary)" }}>
            <i className="fa-solid fa-list-ul" style={{ color: "#f97316", marginRight: 8 }} />
            {t("rubros.titulo")}
          </h3>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "var(--text-muted)",
            fontSize: "1.2rem", cursor: "pointer", lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          <p style={{ margin: "0 0 14px", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
            {t("rubros.descripcion")}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filas.map((f, i) => (
              <div key={f.key} style={{
                display: "flex", flexDirection: "column", gap: 10,
                background: "var(--bg-card-alt)", border: "1px solid var(--border-card)",
                borderRadius: 8, padding: 12,
              }}>
                {/* Fila 1: número + cuenta contable a todo el ancho + eliminar */}
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <div style={{ flex: "0 0 20px", paddingBottom: 9 }}>
                    <span style={{
                      color: "var(--text-muted)", fontSize: "0.8rem", fontWeight: 600,
                    }}>
                      {i + 1}
                    </span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={LABEL_STYLE}>{t("rubros.columnaCuenta")}</label>
                    <CuentaSelectSearch
                      value={f.cuenta}
                      onChange={(id, obj) => actualizarFila(f.key, { cuenta: id, cuentaObj: obj })}
                      tipo={tipo}
                      placeholder={t("rubros.seleccionaCuenta")}
                    />
                  </div>
                  <button
                    type="button" onClick={() => quitarFila(f.key)}
                    disabled={filas.length === 1}
                    title={t("rubros.quitarFila")}
                    style={{
                      flex: "0 0 34px", height: 34, background: "rgba(248,113,113,0.1)",
                      border: "1px solid rgba(248,113,113,0.3)", borderRadius: 6,
                      color: "#f87171", cursor: filas.length === 1 ? "not-allowed" : "pointer",
                      opacity: filas.length === 1 ? 0.4 : 1,
                    }}
                  >
                    <i className="fa-solid fa-trash" style={{ fontSize: "0.75rem" }} />
                  </button>
                </div>

                {/* Fila 2: concepto + monto, alineados debajo de la cuenta */}
                <div style={{ display: "flex", gap: 8, marginLeft: 28 }}>
                  <div style={{ flex: 1.6, minWidth: 0 }}>
                    <label style={LABEL_STYLE}>{t("rubros.columnaConcepto")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("rubros.conceptoPlaceholder")}
                      value={f.concepto}
                      onChange={(e) => actualizarFila(f.key, { concepto: e.target.value })}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: "0.85rem" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ ...LABEL_STYLE, textAlign: "right" }}>{t("rubros.columnaMonto")}</label>
                    <input
                      type="number" min="0" step="0.01"
                      className="form-control"
                      placeholder="0.00"
                      value={f.monto}
                      onChange={(e) => actualizarFila(f.key, { monto: e.target.value })}
                      style={{
                        width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: "0.85rem",
                        textAlign: "right", fontWeight: 600, fontFamily: "monospace",
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button" onClick={agregarFila}
            style={{
              marginTop: 10, padding: "8px 14px", background: "rgba(249,115,22,0.1)",
              border: "1px solid rgba(249,115,22,0.35)", borderRadius: 8,
              color: "#f97316", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 7,
            }}
          >
            <i className="fa-solid fa-plus" />
            {t("rubros.agregarRubro")}
          </button>

          {error && (
            <div style={{ marginTop: 12, color: "#f87171", fontSize: "0.83rem", display: "flex", alignItems: "center", gap: 6 }}>
              <i className="fa-solid fa-circle-exclamation" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 20px", borderTop: "1px solid var(--border-card)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ fontSize: "0.95rem", color: "var(--text-primary)" }}>
            <span style={{ color: "var(--text-muted)", fontSize: "0.75rem", textTransform: "uppercase", marginRight: 8 }}>
              {t("rubros.total")}
            </span>
            <strong style={{ fontFamily: "monospace" }}>
              {total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </strong>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{
              padding: "9px 16px", background: "transparent", border: "1px solid var(--border-card)",
              borderRadius: 8, color: "var(--text-secondary)", fontSize: "0.85rem", cursor: "pointer",
            }}>
              {t("common.cancelar")}
            </button>
            <button onClick={confirmar} style={{
              padding: "9px 16px", background: "#f97316", border: "1px solid #f97316",
              borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer",
            }}>
              <i className="fa-solid fa-check" style={{ marginRight: 6 }} />
              {t("rubros.usarRubros")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
